import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LetterRequestForm = ({ onSubmit, onCancel, loading }) => {
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [errors, setErrors] = useState({});

  const letterPurposes = [
    {
      value: 'banking',
      label: 'Banking Purposes',
      description: 'Employment verification letter for bank accounts, loans, or financial services',
      icon: 'Building2',
    },
    {
      value: 'superannuation_withdrawal',
      label: 'Superannuation Withdrawal',
      description: 'Letter supporting NASFUND superannuation withdrawal request',
      icon: 'Wallet',
    },
    {
      value: 'staff_reference',
      label: 'Staff Reference',
      description: 'Comprehensive reference letter for future employment opportunities',
      icon: 'FileText',
    },
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!selectedPurpose) {
      newErrors.purpose = 'Please select a letter purpose';
    }
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit?.({
      letterPurpose: selectedPurpose,
      additionalDetails: additionalDetails?.trim() || {},
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Request New Letter</h3>
          <p className="text-sm text-muted-foreground">Select the type of letter you need</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Letter Purpose Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Letter Purpose *
          </label>
          <div className="space-y-3">
            {letterPurposes?.map((purpose) => (
              <div
                key={purpose?.value}
                onClick={() => {
                  setSelectedPurpose(purpose?.value);
                  setErrors({ ...errors, purpose: '' });
                }}
                className={`p-4 border rounded-lg cursor-pointer transition-smooth ${
                  selectedPurpose === purpose?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedPurpose === purpose?.value
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={purpose?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-foreground">{purpose?.label}</h4>
                      {selectedPurpose === purpose?.value && (
                        <Icon name="CheckCircle2" size={16} className="text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {purpose?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {errors?.purpose && (
            <p className="text-sm text-error mt-2">{errors?.purpose}</p>
          )}
        </div>

        {/* Additional Details */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Details (Optional)
          </label>
          <textarea
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e?.target?.value)}
            placeholder="Add any specific information you'd like included in the letter..."
            rows={4}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Example: Bank name, specific dates, achievements, or any other relevant details
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-medium mb-1">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>AI generates a professional letter based on your employment data</li>
                <li>Letter is sent to HR Manager for review and approval</li>
                <li>Once approved, you can download the signed letter from your portal</li>
                <li>Letters remain accessible even after resignation</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            type="submit"
            variant="default"
            iconName="Sparkles"
            disabled={loading}
            className="flex-1"
          >
            {loading ? 'Generating Letter...' : 'Generate Letter with AI'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LetterRequestForm;